import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const PREF_ROWS = [
  { key: "assignments", label: "Task assignments", hint: "Email me when a task is assigned to me." },
  { key: "comments", label: "Task comments", hint: "Email me when someone comments on my tasks." },
  { key: "document_uploads", label: "Document uploads", hint: "Email me when a document is uploaded for a client I work on." },
  { key: "due_reminders", label: "Due-date reminders", hint: "Email me 3 days out, 1 day out, and on the day of." },
  { key: "weekly_digest", label: "Weekly digest", hint: "Sunday summary of open work + upcoming deadlines." },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const [prefs, setPrefs] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get("/auth/me/email-prefs").then(({ data }) => setPrefs(data)).catch(() => setPrefs({}));
  }, []);

  const updatePref = async (key, val) => {
    setPrefs((p) => ({ ...p, [key]: val }));
    setSavingKey(key);
    try {
      const { data } = await api.patch("/auth/me/email-prefs", { [key]: val });
      setPrefs(data);
    } catch (e) {
      toast.error("Could not save preference");
      setPrefs((p) => ({ ...p, [key]: !val })); // revert
    } finally {
      setSavingKey(null);
    }
  };

  const sendPreview = async () => {
    setPreview(true);
    try {
      const { data } = await api.post("/auth/me/digest/preview");
      if (data.sent) toast.success("Digest preview sent to your inbox");
      else toast.info(data.reason || "Nothing to send right now");
    } catch (e) {
      toast.error("Could not send preview");
    } finally {
      setPreview(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="settings-page">
      <Card className="p-4 border-border shadow-none">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-3">Profile</h3>
        <div className="grid grid-cols-2 gap-4">
          <Info label="Name" value={user?.full_name} />
          <Info label="Email" value={user?.email} mono />
          <Info label="Role" value={ROLE_LABEL[user?.role]} />
          <Info label="Status" value={user?.status} />
        </div>
      </Card>

      <Card className="p-4 border-border shadow-none" data-testid="settings-email-prefs">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-muted-foreground">Email notifications</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={sendPreview}
            disabled={preview}
            data-testid="settings-send-digest-preview"
            className="h-8"
          >
            {preview ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Send size={12} className="mr-1.5" />}
            Send digest preview
          </Button>
        </div>
        <div className="space-y-3">
          {PREF_ROWS.map((row) => (
            <div key={row.key} className="flex items-center justify-between border-b border-border/60 pb-3 last:border-0 last:pb-0">
              <div className="pr-4">
                <Label className="text-sm font-medium">{row.label}</Label>
                <div className="text-xs text-muted-foreground mt-0.5">{row.hint}</div>
              </div>
              <div className="flex items-center gap-2">
                {savingKey === row.key && <Loader2 size={12} className="animate-spin text-muted-foreground" />}
                <Switch
                  checked={!!prefs?.[row.key]}
                  onCheckedChange={(v) => updatePref(row.key, v)}
                  disabled={prefs === null}
                  data-testid={`email-pref-${row.key}`}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 border-border shadow-none">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-3">Appearance</h3>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Dark mode</Label>
            <div className="text-xs text-muted-foreground mt-0.5">Reduce eye strain during long sessions.</div>
          </div>
          <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="settings-dark-toggle" />
        </div>
      </Card>

      <Card className="p-4 border-border shadow-none">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-3">Keyboard shortcuts</h3>
        <div className="space-y-2 text-sm">
          <Shortcut keys="⌘K" label="Open command palette" />
          <Shortcut keys="⌘/Ctrl + K" label="Global search" />
        </div>
      </Card>

      <Card className="p-4 border-border shadow-none">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-3">Security</h3>
        <a href="/settings/login-history" className="flex items-center justify-between text-sm hover:bg-muted/50 -mx-2 px-2 py-1.5 rounded-md" data-testid="settings-login-history">
          <span>Login history</span>
          <span className="text-xs text-muted-foreground">View all sign-in attempts →</span>
        </a>
      </Card>

      <Card className="p-4 border-border shadow-none">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-3">About</h3>
        <div className="text-sm text-muted-foreground">
          CA OS — AI Operating System for Chartered Accountants. Version <span className="font-mono">1.0.0</span>.
        </div>
      </Card>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="border border-border rounded-md p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-sm mt-1 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Shortcut({ keys, label }) {
  return (
    <div className="flex items-center justify-between">
      <span>{label}</span>
      <kbd className="font-mono text-xs bg-secondary border border-border rounded px-2 py-0.5">{keys}</kbd>
    </div>
  );
}
